import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import Company from "lib/api/Company";
import Client from "lib/api/Client";

import PageLayout from "components/layouts/PageLayout";
import Button from "components/common/Button";
import ButtonGroup from "components/common/ButtonGroup";
import EntityTypeahead from "components/common/EntityTypeahead";
import { filterBarStyles } from "components/common/FilterBar/styles";
import { CompanyContext } from "lib/context/CompanyContext";
import { InfiniteLoader, AutoSizer, Column, Table } from "react-virtualized";
import { exportRecordsAsCSV } from "lib/utils/csv";
import { XMarkIcon } from '@heroicons/react/24/outline';

import "react-virtualized/styles.css";

const COMPANY_LIST_STATE_KEY = "company_list_view_state";

function loadPersistedViewState() {
  try {
    const stored = sessionStorage.getItem(COMPANY_LIST_STATE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    // ignore malformed/missing state
  }
  return null;
}

export default function CompanyList(props) {
  const { company } = useContext(CompanyContext);
  const persistedViewState = loadPersistedViewState();

  const [records, setRecords] = useState([]);
  const [selected, setSelected] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(persistedViewState?.search || "");
  const [loading, setLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [buffer, setBuffer] = useState([]);
  const [clientFilter, setClientFilter] = useState(persistedViewState?.clientFilter || null);

  const [recordsPerPage, setRecordsPerPage] = useState(100);

  async function load() {
    await setLoading(true);
    let params = {
      offset: 0,
      limit: recordsPerPage
    }
    if(clientFilter){
      params["client_uuid"] = clientFilter.uuid;
    } else if(company.uuid && company.client.uuid !== undefined){
      params["client_uuid"] = company.client.uuid;
    }
    if(search){
      params["search"] = search;
    }
    let response = await Company.list(params);
    // let data = response.data.map((record)=>{
    //   record.selected = selectAll;
    //   return record;
    // });
    await setRecords(response.data);
    await setTotal(response.total);
    await setLoading(false);
  }

  function isRowLoaded({ index }) {
    return !!records[index];
  }

  async function loadMoreRows({ startIndex, stopIndex }) {
    if (loading) return;

    await setLoading(true);
    let params = {
      offset: startIndex,
      limit: Math.max(recordsPerPage, stopIndex - startIndex + 1)
    }
    if(clientFilter){
      params["client_uuid"] = clientFilter.uuid;
    } else if(company.uuid && company.client.uuid !== undefined){
      params["client_uuid"] = company.client.uuid;
    }
    if(search){
      params["search"] = search;
    }

    let response = await Company.list(params);
    // data = data.map((record)=>{
    //   record.selected = selectAll
    // });
    if (response.total != total) {
      await setTotal(response.total);
    }

    const newData = response.data;
    setRecords(prevRecords => [...prevRecords, ...newData]);
    await setLoading(false);
  }

  function toggleAll() {
    setSelectAll(!selectAll);
    setRecords(
      records.map((record) => ({ ...record, selected: !selectAll }))
    );
  }

  function toggle(record_uuid) {
    var newRecords = records.map((record) => {
      if (record_uuid === record.uuid) {
        record.selected = !record.selected;
      }
      return record;
    });
    setRecords(newRecords);
  }

  async function fetchAllRecords() {
    const allRecords = [];
    let hasMore = true;
    let offset = 0;
  
    while (hasMore) {
      let params = {
        offset: offset,
        limit: 1000
        //, fields: ["uuid", "name", "code", "id", "created_at", "updated_at", "client_uuid"]
      };

      if(clientFilter){
        params["client_uuid"] = clientFilter.uuid;
      } else if(company.uuid && company.client.uuid !== undefined){
        params["client_uuid"] = company.client.uuid;
      }

      if(search){
        params["search"] = search;
      }
  
      const response = await Company.list(params);
      allRecords.push(...response.data);
  
      hasMore = allRecords.length < response.total;
      offset += response.data.length;
    }
  
    return allRecords;
  }

  async function exportAllRecordsAsCSV() {
    setLoading(true); // Show loading indicator if necessary
    try {
      const allRecords = await fetchAllRecords();
      const columnsToSkip = ["new_key", "client"];

      await exportRecordsAsCSV(allRecords, columnsToSkip, 'company-export.csv');
    } catch (error) {
      console.error('An error occurred while fetching records:', error);
      // Handle errors as appropriate for your application
    }
    setLoading(false); // Hide loading indicator
  }  
  
  useEffect(() => {
    load();
    return ()=>{setLoading(false)};
  }, [company, clientFilter]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        COMPANY_LIST_STATE_KEY,
        JSON.stringify({ search, clientFilter })
      );
    } catch (error) {
      // ignore storage write failures (e.g. quota exceeded)
    }
  }, [search, clientFilter]);

  function clearFilters() {
    setSearch("");
    setClientFilter(null);
  }
const columns = 5;
  return (
    <PageLayout>
      <style>{filterBarStyles}</style>
      <h1>Companies</h1>
      <div className="hr hr--darkest"></div>

      <div className="filter-bar">
        <div className="filter-bar__field">
          <span className="filter-bar__label">Search</span>
          <input
            className="filter-bar__input"
            placeholder="Search by name or code..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
          />
        </div>
        <EntityTypeahead
          label="Client"
          placeholder="Search clients..."
          value={clientFilter}
          onSelect={setClientFilter}
          onClear={() => setClientFilter(null)}
          search={term => Client.get({ search: term, limit: 10 }).then(r => r.data)}
          formatOption={c => `${c.name} (${c.code})`}
        />
        <div className="filter-bar__field" style={{ flex: "0 0 auto" }}>
          <span className="filter-bar__label" style={{ visibility: "hidden" }}>&nbsp;</span>
          <div style={{ display: "flex", gap: 10 }}>
            <ButtonGroup>
              <Button onClick={load}>
                Search
              </Button>
            </ButtonGroup>
            <button className="filter-bar__clear" onClick={clearFilters} title="Clear filters">
              <XMarkIcon style={{ width: '16px', height: '16px', display: 'inline-block', verticalAlign: 'middle' }} />
            </button>
            <button className="filter-bar__clear" onClick={exportAllRecordsAsCSV}>
              Export
            </button>
          </div>
        </div>
        {loading && (
          <div className="loading-container" style={{ alignSelf: "flex-end", marginBottom: 4 }}>
            <div className="loading-icon"></div>
          </div>
        )}
      </div>
      <div>{records.length} of {total} loaded</div>
      <InfiniteLoader
        isRowLoaded={isRowLoaded}
        loadMoreRows={loadMoreRows}
        rowCount={total}
      >
        {({ onRowsRendered, registerChild }) => (
          <AutoSizer>
            {({ height, width }) => {
              return (
                <Table
                  headerHeight={40}
                  width={width}
                  height={height-120}
                  rowCount={total}
                  rowHeight={40}
                  disableHeader={false}
                  rowGetter={({ index }) => records[index] || {}}
                  onRowsRendered={onRowsRendered}
                  ref={registerChild}
                >
                  <Column
                    label="Name"
                    dataKey="name"
                    width={width / columns}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.uuid) {
                        return "Loading...";
                      }
                      return (rowData.name);
                    }}
                  />
                  <Column
                    label="Code"
                    dataKey="code"
                    width={width / columns}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData) {
                        return "Loading....";
                      }
                      return cellData;
                    }}
                  />
                  <Column
                    label="Client"
                    dataKey="client"
                    width={width / columns}
                    cellRenderer={({ rowData, cellData}) => {
                      if (cellData === undefined) {
                        return "Loading...";
                      }
                      return cellData.name ? `${cellData.name}(${cellData.code})` : "";
                    }}
                  />
                  <Column
                    label="Created"
                    dataKey="created_at"
                    width={width / 6}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData) {
                        return "Loading...";
                      }
                      return new Date(cellData).toLocaleString()
                    }}
                  />
                  <Column
                    label=""
                    dataKey="uuid"
                    width={width / 6}
                    cellRenderer={({ rowData, cellData }) => {
                      if (cellData === undefined) {
                        return "";
                      }
                      return (
                        <div style={{ alignItems: "right" }}>
                        <ButtonGroup>
                          <Link className="button m-bot-0" to={`/company/${cellData}/`}>
                            View
                          </Link>
                        </ButtonGroup>
                      </div>
                      );
                    }}
                  />
                </Table>
              );
            }}
          </AutoSizer>
        )}
      </InfiniteLoader>
    </PageLayout>
  );
}